import type React from 'react';
import { useEffect, useRef } from 'react';

import { Input } from '@zillow/constellation-10';

type Props = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-test'?: string;
  'data-field-name'?: string;
  'data-index'?: number;
  name?: string;
};

/**
 * Formats a phone number string to (XXX) XXX-XXXX format
 * Handles various input formats including E.164 (+14155551234)
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  let digits = value.replace(/\D/g, '');

  // Strip leading country code (1) if present and we have 11 digits
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10);

  // Format based on how many digits we have
  if (limitedDigits.length === 0) {
    return '';
  }
  if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`;
  }
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
}

export default function PhoneInput({ value, onChange, error, disabled, ...rest }: Props): JSX.Element {
  const hasNormalized = useRef(false);

  // Normalize unformatted phone numbers on mount
  useEffect(() => {
    if (!hasNormalized.current && value) {
      const formatted = formatPhoneNumber(value);
      if (formatted !== value && formatted.length === 14) {
        // Only normalize if we have a complete phone number (14 chars = "(XXX) XXX-XXXX")
        hasNormalized.current = true;
        const syntheticEvent = {
          currentTarget: {
            value: formatted,
            dataset: rest['data-field-name'] ? { fieldName: rest['data-field-name'] } : {},
          },
          target: { value: formatted },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
  }, [value, onChange, rest]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const rawValue = event.currentTarget.value;
    const formattedValue = formatPhoneNumber(rawValue);

    // Create a proxy that overrides only the value while preserving access to the original element
    const currentTargetProxy = new Proxy(event.currentTarget, {
      get(target, prop) {
        if (prop === 'value') {
          return formattedValue;
        }
        return target[prop as keyof HTMLInputElement];
      },
    });

    const targetProxy = new Proxy(event.target as HTMLInputElement, {
      get(target, prop) {
        if (prop === 'value') {
          return formattedValue;
        }
        return target[prop as keyof HTMLInputElement];
      },
    });

    // Create a synthetic event with the proxied targets
    const syntheticEvent = {
      ...event,
      currentTarget: currentTargetProxy,
      target: targetProxy,
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  }

  const displayValue = formatPhoneNumber(value || '');

  return (
    <Input
      value={displayValue}
      onChange={handleChange}
      type="tel"
      inputMode="tel"
      error={error}
      disabled={disabled}
      {...rest}
    />
  );
}

// Export the format function for use elsewhere
export { formatPhoneNumber };
